import React, { Component } from "react";

import Constants from "../Constants.js";
import Styles from "./BoardStyles.js";
import Levels from "../Levels.js";
import Sounds from "../Sounds.js";
import LocalStorage from "../LocalStorage.js";

import OuterTile from "./OuterTile.js";
import InnerTile from "./InnerTile.js";
import Letter from "./Letter.js";
import Tutorial from "./Tutorial.js";
import BoardButtons from "./BoardButtons.js";
import ThemeHeader from "./ThemeHeader.js";
import PlatformTest from '../PlatformTest.js';

import './Board.css';



function mod(m, n) {
    return ((m % n) + n) % n;
}

function getLevelStates(l) {
    const level = Levels[l];
    const height = level.layout.length;
    const width = level.layout[0].length;

    // Fill board with letters
    let letters = [];
    for (let i = 0; i < height; i++) {
        for (let j = 0; j < width; j++) {
            if (level.layout[i][j]) {
                letters.push({
                    letter: level.layout[i][j],
                    row: i,
                    col: j,
                    active: false,
                    hidden: true,
                    pulsing: false,
                });
            }
        }
    }

    // Fill board with letters
    let bounds = [];
    for (let i = 0; i < height; i++) {
        bounds.push([]);
        for (let j = 0; j < width; j++) {
            const b = (level.layout[i][j] ? 1 : 0);
            bounds[i][j] = b;
        }
    }

    return {
        words: level.words,
        colors: level.colors,
        width: width,
        height: height,
        letters: letters,
        bounds: bounds,
        theme: level.theme,
    };
}





class Board extends Component {

    constructor(props) {
        super(props);

        // Get elements
        this.getTiles = this.getTiles.bind(this);
        this.getLetters = this.getLetters.bind(this);
        this.getBoundsAtPosition = this.getBoundsAtPosition.bind(this);
        this.getLetterAtPosition = this.getLetterAtPosition.bind(this);

        // Modify selected letters
        this.addToSelected = this.addToSelected.bind(this);
        this.clearSelected = this.clearSelected.bind(this);
        this.applyGravity = this.applyGravity.bind(this);
        this.hideSelected = this.hideSelected.bind(this);
        this.fadeInLetters = this.fadeInLetters.bind(this);
        this.fadeOutLetters = this.fadeOutLetters.bind(this);
        this.showLetters = this.showLetters.bind(this);
        this.hideLetters = this.hideLetters.bind(this);
        this.stopPulsing = this.stopPulsing.bind(this);

        // Buttons
        this.hideButtons = this.hideButtons.bind(this);
        this.showButtons = this.showButtons.bind(this);
        this.hideMenuButton = this.hideMenuButton.bind(this);
        this.showMenuButton = this.showMenuButton.bind(this);
        this.hideHintButton = this.hideHintButton.bind(this);
        this.showHintButton = this.showHintButton.bind(this);
        this.hideRestartButton = this.hideRestartButton.bind(this);
        this.showRestartButton = this.showRestartButton.bind(this);

        // Show and hide board
        this.showBoard = this.showBoard.bind(this);
        this.hideBoard = this.hideBoard.bind(this);

        // Level status
        this.advanceLevel = this.advanceLevel.bind(this);
        this.updateLevelStates = this.updateLevelStates.bind(this);
        this.getSelectedWord = this.getSelectedWord.bind(this);

        // Touch events
        this.getRelativeCoordinates = this.getRelativeCoordinates.bind(this);
        this.handleStart = this.handleStart.bind(this);
        this.handleMove = this.handleMove.bind(this);
        this.handleEnd = this.handleEnd.bind(this);

        // Decide whether a word exists in the puzzle
        this.wordExists = this.wordExists.bind(this);
        this.wordExistsHelper = this.wordExistsHelper.bind(this);

        // Button functions
        this.handleRestart = this.handleRestart.bind(this);
        this.handleMenuOpen = this.handleMenuOpen.bind(this);
        this.handleHint = this.handleHint.bind(this);
        this.rotateRestartButton = this.rotateRestartButton.bind(this);

        this.onAdLoaded = this.onAdLoaded.bind(this);
        this.onAdPresent = this.onAdPresent.bind(this);

        // Pulsing
        this.pulseLetters = this.pulseLetters.bind(this);

        // Tutorial
        this.initTutorial = this.initTutorial.bind(this);

        // Intervals
        this.startIntervals = this.startIntervals.bind(this);
        this.restartIntervals = this.restartIntervals.bind(this);

        // Get local storage
        let data = LocalStorage.getData();

        let currentLevelIndex = 0;
        for (let i = 0; i < Levels.length; i++) {
            if (Levels[i].theme === data.currentLevel) {
                currentLevelIndex = i;
                break;
            }
        }

        // Get level states
        const levelStates = getLevelStates(currentLevelIndex);

        // AdMob set-up
        let rewardedId = undefined;
        let interstitialId = undefined;
        if (PlatformTest.ios()) { // iOS
            rewardedId = 'ca-app-pub-9606736113132446/5445385339';
            interstitialId = 'ca-app-pub-9606736113132446/5611678939';
        }
        else if (PlatformTest.android()) { // Android
            rewardedId = 'ca-app-pub-9606736113132446/8291040523';
            interstitialId = 'ca-app-pub-9606736113132446/3017305183';
        }

        this.state = {
            // AdMob ad ID's
            rewardedId: rewardedId,
            interstitialId: interstitialId,
            // Level-specific states
            ...levelStates,
            level: currentLevelIndex,
            solvable: true,
            // Mouse interaction
            mouseDown: false,
            touchValid: true,
            transitioning: false,
            selected: [],
            // Animation
            animating: false,
            restartRotations: 0,
            // Tutorial states
            tutorialMode: this.props.tutorialMode,
            tutorialStage: 0,
            pulsingLetters: [],
            // Intervals
            restartInterval: undefined,
            pulseInterval: undefined,
            hintInterval: undefined,
            // Timestamp of last interaction
            lastInteraction: new Date().getTime(),
            // Whether the board is showing
            showing: false,
            // Whether buttons are showing
            showingMenuButton: false,
            showingHintButton: false,
            showingRestartButton: false,
            // Whether buttons are pulsing
            pulsingHintButton: false,
            pulsingRestartButton: false,
        };

        // Refs
        this.boardWrapper = React.createRef();
        this.boardTransform = React.createRef();
        this.boardContainer = React.createRef();
        this.levelNumberTransform = React.createRef();
        this.levelNumber = React.createRef();
        this.tutorial = React.createRef();
    }

    startHintPulseTimer() {
        this.setState({
            lastInteraction: new Date().getTime(),
            pulsingHintButton: false,
        }, () => {
            let dur = Constants.stuckDuration;
            setTimeout(() => {
                let now = new Date().getTime();
                if ((now - this.state.lastInteraction) >= dur - 1000) {
                    this.setState({ pulsingHintButton: true })
                }
            }, dur);
        });
    }

    pulseLetters() {
        // For each pulsing letter
        for (let i = 0; i < this.state.pulsingLetters.length; i++) {
            let p = this.state.pulsingLetters[i];
            // Depress letters
            setTimeout(
                function(i, p) {
                    let letters = [...this.state.letters];
                    letters[p].pulsing = true;
                    this.setState({letters: letters});

                    if (window.TapticEngine) window.TapticEngine.impact({ style: "light" });

                    // Undepress letters
                    setTimeout(
                        function(i, p) {
                            let letters = [...this.state.letters];
                            if (letters[p]) letters[p].pulsing = false;
                            this.setState({letters: letters});

                        }.bind(this), 1200, i, p
                    );

                }.bind(this), i * 140, i, p
            );
        }
    }

    startIntervals() {
        // Pulse letters
        this.pulseLetters();
        let pulseInterval = setInterval(() => {
            this.pulseLetters();
        }, 3000);

        // Update intervals in state
        this.setState({
            pulseInterval: pulseInterval,
        })
    }
    restartIntervals() {
        // Clear intervals
        clearInterval(this.state.pulseInterval);

        // Start intervals
        this.startIntervals();
    }

    onAdLoaded(data) {
        if (data.adType === 'rewardvideo') {
            if (window.AdMob) window.AdMob.showRewardVideoAd();
        }
    }
    onAdPresent(data) {
        if (data.adType === 'rewardvideo') {
            this.showHint();
        }
    }

    // On component mount
    componentDidMount() {
        this.startIntervals();

        // Add ad event listeners for loading and completion
        document.addEventListener('onAdLoaded', this.onAdLoaded, false);
        document.addEventListener('onAdPresent', this.onAdPresent, false);
    }

    // Remove ad event listeners
    componentWillUnmount() {
        document.removeEventListener('onAdLoaded', this.onAdLoaded, false);
        document.removeEventListener('onAdPresent', this.onAdPresent, false);
    }

    // Initialize the tutorial
    initTutorial(callback) {
        // Hide buttons
        this.hideButtons();
        // Set to level 0
        this.updateLevelStates(0, () => {
            // Set tutorial states
            this.setState({
                tutorialMode: true,
                tutorialStage: 0,
                pulsingLetters: [],
            },
            () => {
                // Call callback, if it exists
                if (typeof callback === 'function') callback();
            });
        });
    }

    rotateRestartButton() {
        this.setState({restartRotations: this.state.restartRotations + 1});
    }
    // Showing and hiding buttons
    showRestartButton() {
        this.setState({ showingRestartButton: true });
    }
    hideRestartButton() {
        this.setState({ showingRestartButton: false });
    }
    showMenuButton() {
        this.setState({ showingMenuButton: true });
    }
    hideMenuButton() {
        this.setState({ showingMenuButton: false });
    }
    showHintButton() {
        this.setState({ showingHintButton: true });
    }
    hideHintButton() {
        this.setState({ showingHintButton: false });
    }
    showButtons() {
        this.showMenuButton();
        this.showRestartButton();
        this.showHintButton();
    }
    hideButtons() {
        this.hideMenuButton();
        this.hideRestartButton();
        this.hideHintButton();
    }

    // Show the board
    showBoard() {
        this.boardWrapper.current.style.transform = 'scale(1.0)';
        this.setState({showing: true});

        // this.fadeInLetters();
        if (!this.state.tutorialMode) {
            this.showButtons();
            this.showLetters();
        }
    }
    // Hide the board
    hideBoard() {
        this.boardWrapper.current.style.transform = 'scale(0.7)';
        this.setState({showing: false});

        this.hideLetters();
        this.hideButtons();
        //this.fadeOutLetters();
    }

    // Decide whether a word exists in the puzzle
    wordExists(word) {
        return this.wordExistsHelper(word, []);
    }
    // Recursive helper function for wordExists
    wordExistsHelper(word, used) {
        // Base case
        if (word === '') {
            return used;
        }
        // Recursive case
        else {
            // The letter we're looking for
            const targetLetter = word[0];
            // Valid adjacent letters
            const validAdjacent = [];

            const letters = this.state.letters;

            for (let i = 0; i < letters.length; i++) {
                const letter = letters[i];

                if (letter.letter === targetLetter) {
                    // No letters have been used; doesn't need to be adjacent
                    if (used.length === 0) {
                        if (!letter.hidden)
                            validAdjacent.push(letter);
                    }
                    // Not the first letter
                    else {
                        const lastLetter = used[used.length - 1];

                        // If letter is adjacent, unused, not hidden, and the correct letter
                        const isAdjacent = Math.abs(letter.col - lastLetter.col) <= 1 &&
                            Math.abs(letter.row - lastLetter.row) <= 1;
                        const isSelf = letter.row === lastLetter.row &&
                            letter.col === lastLetter.col;
                        const isHidden = letter.hidden;

                        // Whether this letter is being used
                        let isUsed = false;
                        for (let j = 0; j < used.length; j++) {
                            if (used[j].col === letter.col && used[j].row === letter.row) {
                                isUsed = true;
                                break;
                            }
                        }

                        if (isAdjacent && !isUsed && !isHidden && !isSelf)
                            validAdjacent.push(letter);
                    }
                }

            }

            // If there are no valid adjacent letters, return false
            if (validAdjacent.length === 0) {
                return false;
            }
            // Check whether any adjacent letters form the word
            else {
                for (let i = 0; i < validAdjacent.length; i++) {
                    let newWord = word.substring(1);
                    let newAdjacent = used.concat(validAdjacent[i]);
                    let exists = this.wordExistsHelper(newWord, newAdjacent);
                    if (exists)
                        return exists;
                }
                return false;
            }
        }
    }

    // Update level states
    updateLevelStates(level, callback) {
        if (typeof level === 'undefined') level = this.state.level;
        const levelStates = getLevelStates(level);

        this.setState({
            ...levelStates,
            level: level,
            solvable: true,
        }, () => {
            this.startHintPulseTimer();
            if (typeof callback === 'function') callback();
        });
    }

    // Get the board bounds (0 or 1) at a certain coordinate
    getBoundsAtPosition(row, col) {
        // If this position is out of bounds
        if (
            row < 0 ||
            row >= this.state.height ||
            col < 0 ||
            col >= this.state.width
        ) {
            return 0;
        }
        // This position is in bounds
        else {
            return this.state.bounds[row][col];
        }
    }

    // Get tile elements
    getTiles() {
        let tiles = [];
        let key = 0;
        for (let i = 0; i < this.state.height; i++) {
            for (let j = 0; j < this.state.width; j++) {
                // Directly adjacent
                const top = this.getBoundsAtPosition(i - 1, j);
                const right = this.getBoundsAtPosition(i, j + 1);
                const bottom = this.getBoundsAtPosition(i + 1, j);
                const left = this.getBoundsAtPosition(i, j - 1);

                // Diagonal
                const topLeft = this.getBoundsAtPosition(i - 1, j - 1);
                const topRight = this.getBoundsAtPosition(i - 1, j + 1);
                const bottomRight = this.getBoundsAtPosition(i + 1, j + 1);
                const bottomLeft = this.getBoundsAtPosition(i + 1, j - 1);

                // This is an inner tile
                if (this.state.bounds[i][j]) {
                    const topLeftRounded = !top && !left;
                    const topRightRounded = !top && !right;
                    const bottomRightRounded = !bottom && !right;
                    const bottomLeftRounded = !bottom && !left;

                    tiles.push(
                        <InnerTile
                            key={"tile" + key}
                            transitioning={this.state.transitioning}
                            topLeftRounded={topLeftRounded}
                            topRightRounded={topRightRounded}
                            bottomRightRounded={bottomRightRounded}
                            bottomLeftRounded={bottomLeftRounded}
                            row={i}
                            col={j}
                            colors={this.state.colors}
                        />
                    );
                }
                // This is an outer tile
                else {
                    const topLeftRounded = top && left && topLeft;
                    const topRightRounded = top && right && topRight;
                    const bottomRightRounded = bottom && right && bottomRight;
                    const bottomLeftRounded = bottom && left && bottomLeft;

                    tiles.push(
                        <OuterTile
                            key={"tile" + key}
                            transitioning={this.state.transitioning}
                            topLeftRounded={topLeftRounded}
                            topRightRounded={topRightRounded}
                            bottomRightRounded={bottomRightRounded}
                            bottomLeftRounded={bottomLeftRounded}
                            row={i}
                            col={j}
                            colors={this.state.colors}
                        />
                    );
                }

                key++;
            }
        }

        return tiles;
    }

    // Get letter elements
    getLetters() {
        return this.state.letters.map((letter, index) =>
            <Letter
                key={"letter-" + this.state.level + '-' + index}
                row={letter.row}
                col={letter.col}
                letter={letter.letter}
                active={letter.active}
                hidden={letter.hidden}
                pulsing={letter.pulsing}
                colors={this.state.colors}
            />
        );
    }

    // Get the letter at a given position (or false if none exists)
    getLetterAtPosition(letters, row, col) {
        for (let i = 0; i < letters.length; i++) {
            const l = letters[i];
            if (l.row === row && l.col === col && !l.hidden) return l;
        }
        return false;
    }

    // Get a letter's index given its row and column
    getLetterIndex(row, col) {
        for (let i = 0; i < this.state.letters.length; i++) {
            const l = this.state.letters[i];
            if (l.row === row && l.col === col) return i;
        }
        return false;
    }

    // Add a letter to the selection
    addToSelected(row, col) {
        Sounds.play('selectLetter');

        if (window.TapticEngine) window.TapticEngine.gestureSelectionChanged();
        //if (window.TapticEngine) window.TapticEngine.impact({ style: "light" });

        // Get the index of this letter
        let letterIndex = this.getLetterIndex(row, col);

        // Update selected array
        let selected = this.state.selected.concat(letterIndex);

        // Update letters
        let letters = [...this.state.letters];
        let letter = { ...letters[letterIndex] };
        letter.active = true;
        letters[letterIndex] = letter;

        // Update state variables
        this.setState({
            letters: letters,
            selected: selected
        });
    }

    // Clear selected
    clearSelected(callback) {
        // Create a copy of letters
        let letters = [...this.state.letters];
        for (let i = 0; i < letters.length; i++) {
            letters[i].active = false;
        }

        // Update state variables
        this.setState(
            {
                selected: [],
                letters: letters
            },
            () => { if (typeof callback === "function") callback(); }
        );
    }

    advanceLevel() {
        // Play sound
        Sounds.play('levelComplete');

        if (window.TapticEngine) window.TapticEngine.notification({ type: "success" });
        //if (window.TapticEngine) window.TapticEngine.unofficial.strongBoom();

        // Prepare interstitial if ads have not been removed
        if (!this.props.data.removedAds) {
            if (window.AdMob) {
                window.AdMob.prepareInterstitial({
                    adId: this.state.interstitialId,
                    autoShow: false,
                });
            }
        }

        // Update local storage
        let data = LocalStorage.getData();
        let nextLevel = mod(this.state.level + 1, Levels.length);

        // If paid levels are not unlocked
        if (!data.paidLevelsUnlocked) {
            nextLevel = mod(nextLevel, Constants.freeLevels);
            if (nextLevel === 0) {
                if (typeof this.props.onOutOfLevels === 'function')
                    this.props.onOutOfLevels();
            }
        }

        // Update current level
        let nextTheme = Levels[nextLevel].theme;
        data.currentLevel = nextTheme;

        // If this level was formerly unbeat, save it as beat and give them a hint
        if (!(data.levelsBeat.indexOf(this.state.theme) > -1)) {
            data.levelsBeat.push(this.state.theme);
        }

        // Check if they've beat the game
        if (data.levelsBeat.length === Levels.length) {
            if (typeof this.props.onWin === 'function')
                this.props.onWin();
        }

        // Check if they should rate the game
        if (nextLevel === Constants.requestRatingLevel && (!data.rated && !data.requestedRating)) {
            // Mark that a rating has been requested, to not ask again
            data.requestedRating = true;
            if (typeof this.props.onRequestRating === 'function')
                this.props.onRequestRating();
        }

        // Update local storage
        if (typeof this.props.onUpdateData === 'function')
            this.props.onUpdateData(data);

        // Update state
        this.setState({
            animating: true,
            transitioning: true,
            level: nextLevel,
        });

        this.hideButtons();

        let additionalDelay = (this.state.tutorialMode ? 2000 : 0);

        // Hide board
        setTimeout(() => {
            this.boardTransform.current.style.transform = 'translateY(-100%) rotate(30deg)';
            //this.boardContainer.current.style.top = '-80%';
            //this.boardContainer.current.style.transform = 'translate(-50%, -50%) rotate(30deg)';
        }, 1000 + additionalDelay );

        // Make board invisible and move it to the bottom
        // Show level number
        setTimeout(() => {
            this.boardContainer.current.style.opacity = '0';
            this.boardTransform.current.style.transform = 'translateY(100%) rotate(-30deg)';
            this.updateLevelStates();

            this.levelNumber.current.style.opacity = '1';
            this.levelNumberTransform.current.style.transform = 'translateY(0) rotate(0)';
            //this.levelNumber.current.style.top = '50%';
            //this.levelNumber.current.style.transform = 'translate(-50%, -50%) rotate(0deg)';
        }, 2000 + additionalDelay );

        // Hide level number
        setTimeout(() => {
            //this.levelNumber.current.style.top = '-80%';
            //this.levelNumber.current.style.transform = 'translate(-50%, -50%) rotate(30deg)';
            this.levelNumberTransform.current.style.transform = 'translateY(-100%) rotate(30deg)';
        }, 4000 + additionalDelay );

        // Show board
        setTimeout(() => {
            this.boardContainer.current.style.opacity = '1';
            this.boardTransform.current.style.transform = 'translateY(0) rotate(0deg)';

            this.levelNumber.current.style.opacity = '0';
            //this.levelNumber.current.style.top = '180%';
            //this.levelNumber.current.style.transform = 'translate(-50%, -50%) rotate(-30deg)';
            this.levelNumberTransform.current.style.transform = 'translateY(100%) rotate(-30deg)';
        }, 5000 + additionalDelay );

        // Update transitioning state
        setTimeout(() => {
            this.setState({
                transitioning: false
            })
        }, 6000 + additionalDelay);

        // Fade in letters
        setTimeout(() => {
            this.setState({animating: false});
            this.showButtons();
            this.fadeInLetters(() => {
                // Don't show an ad after the first level
                if (nextLevel !== 1) {
                    if (!this.props.data.removedAds) {
                        if (window.AdMob) {
                            window.AdMob.showInterstitial();
                        }
                    }
                }
            });
        }, 7000 + additionalDelay);
    }

    // Get the current selected word
    getSelectedWord() {
        let word = '';
        for (let i = 0; i < this.state.selected.length; i++) {
            word += this.state.letters[this.state.selected[i]].letter;
        }
        return word;
    }

    // Get the current hint word
    getHintWord() {
        let word = '';
        for (let i = 0; i < this.state.pulsingLetters.length; i++) {
            word += this.state.letters[this.state.pulsingLetters[i]].letter;
        }
        return word;
    }

    // Hide selected
    hideSelected() {
        const letters = [...this.state.letters];
        const selectedLength = this.state.selected.length;

        // Form word from selected
        let word = this.getSelectedWord();

        let checkForWin = () => {

            const solvable = checkForSolvable();
            this.setState({
                solvable: solvable
            });

            if (this.state.tutorialMode && !solvable) {
                this.tutorial.current.setStage(6);
                this.showRestartButton();
            }

            // Check for win
            const letters = this.state.letters;
            for (let i = 0; i < letters.length; i++) {
                if (letters[i].hidden === false) {
                    return false;
                }
            }

            // They cleared this level
            this.advanceLevel();

            // Tutorial advancement
            if (this.state.tutorialMode && this.state.tutorialStage === 8) {
                this.tutorial.current.advanceStage();
            }
            return true;
        }

        let checkForSolvable = () => {
            const words = this.state.words;
            for (let i = 0; i < words.length; i++) {
                if (this.wordExists(words[i]))
                    return true;
            }
            return false;
        }

        let tutorialValid = !this.state.tutorialMode;
        // Tutorial advancement
        if (this.state.tutorialMode) {
            if (this.state.tutorialStage === 4 && word === 'hash') {
                tutorialValid = true;
                this.tutorial.current.advanceStage();
            }
            if (this.state.tutorialStage === 5 && word === 'symbol') {
                tutorialValid = true;
                //this.tutorial.current.advanceStage();
            }
            if (this.state.tutorialStage === 7 && word === 'social') {
                tutorialValid = true;
                this.tutorial.current.advanceStage();
            }
        }
        if (this.state.tutorialStage > 7)
            tutorialValid = true;

        // Decide whether selected forms a real word
        const wordValid = this.state.words.indexOf(word) > -1;
        if (wordValid && tutorialValid) {

            Sounds.play('clearWord');
            // if (window.TapticEngine) window.TapticEngine.unofficial.strongBoom();
            if (window.TapticEngine) window.TapticEngine.impact({ style: "heavy" });

            this.setState({
                animating: true,
            });
            // For each selected letter
            for (let i = 0; i < selectedLength; i++) {
                // Hide this letter
                setTimeout(
                    function(i, selectedLength) {
                        const letterIndex = this.state.selected[i];
                        const letter = { ...letters[letterIndex] };
                        letter.hidden = true;
                        letters[letterIndex] = letter;
                        this.setState({
                            letters: letters
                        });

                        if (i === selectedLength - 1) {
                            this.setState({animating: false});
                            this.applyGravity(() => {
                                this.clearSelected(checkForWin);
                            });
                            //this.clearSelected(checkForWin);
                        }
                    }.bind(this),
                    i * 80,
                    i,
                    selectedLength
                );
            }
        }
        // Word is not valid
        else {
            this.clearSelected();
        }
    }

    // Fade in letters
    fadeInLetters(callback) {

        this.setState({animating: true});

        // Create a copy of letters
        let letters = [...this.state.letters];

        let dim = this.state.width + this.state.height - 1;
        for (let i = 0; i < dim; i++) {
            setTimeout(
                function(letters, i) {

                    let row = i;
                    let col = 0;
                    for (let j = 0; j <= i; j++) {
                        const index = this.getLetterIndex(row, col);
                        if (index !== false) {
                            letters[index].hidden = false;
                        }
                        row--;
                        col++;
                    }

                    this.setState({
                        letters: letters
                    });

                    // If this is the last tile
                    if (i === dim - 1) {
                        this.setState({animating: false}, () => {
                            if (typeof callback === 'function') callback();
                        });
                    }
                }.bind(this),
                i * 40,
                letters, i
            );
        }
    }

    // Hide letters instantly
    hideLetters(callback) {
        let letters = [...this.state.letters];

        for (let i = 0; i < this.state.height; i++) {
            for (let j = 0; j < this.state.width; j++) {
                const index = this.getLetterIndex(i, j);
                if (index !== false) {
                    letters[index].hidden = true;
                }
            }
        }

        this.setState({
            letters: letters
        }, () => {
            if (typeof callback === 'function') callback();
        });
    }

    // Show letters instantly
    showLetters(callback) {
        let letters = [...this.state.letters];

        for (let i = 0; i < this.state.height; i++) {
            for (let j = 0; j < this.state.width; j++) {
                const index = this.getLetterIndex(i, j);
                if (index !== false) {
                    letters[index].hidden = false;
                }
            }
        }

        this.setState({
            letters: letters
        }, () => {
            if (typeof callback === 'function') callback();
        });
    }

    // Fade out letters
    fadeOutLetters(callback) {

        this.setState({animating: true});

        // Create a copy of letters
        let letters = [...this.state.letters];

        let dim = this.state.width + this.state.height - 1;
        for (let i = dim-1; i >= 0; i--) {
            setTimeout(
                function(letters, i) {

                    let row = i;
                    let col = 0;
                    for (let j = 0; j <= i; j++) {
                        const index = this.getLetterIndex(row, col);
                        if (index !== false) {
                            letters[index].hidden = true;
                        }
                        row--;
                        col++;
                    }

                    this.setState({
                        letters: letters
                    });

                    // If this is the last tile
                    if (i === dim - 1) {
                        this.setState({animating: false}, () => {
                            if (typeof callback === 'function') callback();
                        });
                    }
                }.bind(this),
                i * 40,
                letters, i
            );
        }
    }

    // Apply gravity to remaining letters
    applyGravity(callback) {
        let layers = [];

        // Create a copy of letters
        let letters = [...this.state.letters];

        // For each column
        for (let j = 0; j < this.state.width; j++) {
            let layer = 0;

            // For each row, starting at the bottom
            for (let i = this.state.height - 1; i >= 0; i--) {
                const letter = this.getLetterAtPosition(letters, i, j);

                // If there is a letter at this position which is not hidden
                if (letter && !letter.hidden) {
                    // For each spot below this letter
                    for (let k = i + 1; k <= this.state.height; k++) {
                        let letterBelow = this.getLetterAtPosition(letters, k, j);
                        let boundsBelow = this.getBoundsAtPosition(k, j);

                        // The letter cannot be moved further down
                        // Move letter to the spot above (k, j)
                        if (
                            (letterBelow && !letterBelow.hidden) ||
                            !boundsBelow
                        ) {
                            letter.row = k - 1;
                            if (i !== k - 1) {
                                if (typeof layers[layer] === "undefined")
                                    layers[layer] = [];
                                layers[layer].push({
                                    from: { row: i, col: j },
                                    to: { row: k - 1, col: j }
                                });
                                layer++;
                            }
                            break;
                        }
                    }
                }
            }
        }

        this.setState(
            { letters: letters },
            () => { if (typeof callback === "function") callback(); }
        );
    }

    // Stop all letters from pulsing
    stopPulsing() {
        let letters = [...this.state.letters];
        for (let i = 0; i < letters.length; i++) {
            letters[i].pulsing = false;
        }
        this.setState({
            letters: letters,
            pulsingLetters: []
        });
    }



    /*
    * Handle touch events
    */

    // Touch start
    handleStart(x, y) {
        if (window.TapticEngine) window.TapticEngine.gestureSelectionStart();
        if (!this.state.animating) {
            const row = Math.floor(y);
            const col = Math.floor(x);
            this.clearSelected(() => {
                const letter = this.getLetterAtPosition(this.state.letters, row, col);
                if (letter !== false) {
                    this.stopPulsing();
                    this.addToSelected(row, col);
                    this.startHintPulseTimer();
                }
            });
        }
    }

    // Touch move
    handleMove(x, y) {
        if (!this.state.animating) {
            // If no letter is yet selected, check whether this could be the first letter
            const selected = this.state.selected;
            if (selected.length === 0) {
                const row = Math.floor(y);
                const col = Math.floor(x);
                const letter = this.getLetterAtPosition(this.state.letters, row, col);
                if (letter !== false) {
                    this.stopPulsing();
                    this.addToSelected(row, col);
                }
                return;
            }

            // If the previous letter exists, store it
            const lastSelected = selected[selected.length - 1];
            const lastLetter = this.state.letters[lastSelected];

            // Distance and direction from last letter
            const deltaX = x - (lastLetter.col + 0.5);
            const deltaY = y - (lastLetter.row + 0.5);
            const dist = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
            const angle = mod(
                Math.atan2(deltaY, deltaX) + Math.PI * 0.125,
                Math.PI * 2
            );
            const dir = Math.floor((8 * angle) / (Math.PI * 2));

            // Row/column increment in each direction
            // Direction 0: to the right, moving clockwise
            const inc = [
                { row: 0, col: 1 },
                { row: 1, col: 1 },
                { row: 1, col: 0 },
                { row: 1, col: -1 },
                { row: 0, col: -1 },
                { row: -1, col: -1 },
                { row: -1, col: 0 },
                { row: -1, col: 1 }
            ];

            // Row/column of next letter
            const newRow = lastLetter.row + inc[dir].row;
            const newCol = lastLetter.col + inc[dir].col;
            const letterIndex = this.getLetterIndex(newRow, newCol);

            const alreadySelected = selected.indexOf(letterIndex) > -1;
            const letter = this.getLetterAtPosition(this.state.letters, newRow, newCol);

            const isAdjacent = Math.abs(lastLetter.col - newCol) <= 1 &&
                Math.abs(lastLetter.row - newRow) <= 1;

            const distThreshold = Math.sqrt(0.55);
            const backtrackingThreshold = Math.sqrt(0.60);


            // Check for backtracking
            if (selected.length >= 2) {
                if (
                    selected[selected.length - 2] === letterIndex &&
                    letter !== false &&
                    dist > backtrackingThreshold &&
                    isAdjacent
                ) {
                    let lastSelectedIndex = selected[selected.length - 1];
                    // Create a copy of letters
                    let newLetters = [...this.state.letters];
                    let newSelected = [...this.state.selected];
                    newLetters[lastSelectedIndex].active = false;
                    newSelected.pop();

                    // Update state variables
                    this.setState({
                        selected: newSelected,
                        letters: newLetters
                    });
                }
            }

            // If this is a valid letter to selected
            if (
                !alreadySelected &&
                letter !== false &&
                dist > distThreshold &&
                isAdjacent
            ) {
                this.addToSelected(newRow, newCol);
            }
        }
    }

    // Touch end
    handleEnd() {
        if (window.TapticEngine) window.TapticEngine.gestureSelectionEnd();
        if (!this.state.animating) {
            this.hideSelected();
        }
    }

    // Get coordinates relative to element
    getRelativeCoordinates(el, x, y) {
        const rect = el.getBoundingClientRect();

        const xShifted = x - rect.left;
        const yShifted = y - rect.top;
        const xRelative = xShifted / Constants.tileSize;
        const yRelative = yShifted / Constants.tileSize;

        return {
            x: xRelative,
            y: yRelative
        };
    }

    // Handle restarting
    handleRestart() {
        if (!this.state.animating && !this.state.transitioning) {
            Sounds.play('restart');

            // Coolmath replay event
            if (typeof window.top.cmgGameEvent === 'function') {
                window.top.cmgGameEvent('replay', (this.state.level + 1).toString())
            }

            if (window.TapticEngine) {
                window.TapticEngine.impact({ style: "light" });
            }

            // Update last ineraction time, clear pulsing letters
            this.setState({
                pulsingLetters: []
            });
            this.startHintPulseTimer();

            this.rotateRestartButton();
            this.fadeOutLetters(() => {
                this.updateLevelStates(this.state.level, () => {
                    this.fadeInLetters();

                    // Tutorial advancement
                    if (this.state.tutorialMode && this.state.tutorialStage === 6) {
                        this.tutorial.current.advanceStage();
                    }
                });
            });
        }
    }

    // Handle opening the menu
    handleMenuOpen() {
        if (!this.state.animating && !this.state.transitioning) {

            Sounds.play('tap');

            if (window.TapticEngine) {
                window.TapticEngine.impact({ style: "light" });
            }

            this.setState({
                tutorialMode: false,
                pulsingLetters: []
            });
            this.props.onMenuOpen();
        }
    }

    showHint() {
        // Look for first existing word and pulse its letters
        const words = this.state.words;
        for (let i = words.length-1; i >= 0; i--) {
            let exists = this.wordExists(words[i]);
            if (exists) {

                // Compile pulsing letters
                let pulsing = [];
                for (let j = 0; j < exists.length; j++) {
                    let letter = exists[j];
                    pulsing.push(this.getLetterIndex(letter.row, letter.col));
                }

                // Update state
                this.setState({
                    pulsingLetters: pulsing
                }, () => {
                    this.restartIntervals();
                });

                break;
            }
        }
    }

    // Handle use of hint
    handleHint() {
        if (!this.state.animating && !this.state.transitioning && this.state.solvable && this.state.pulsingLetters.length === 0) {
            Sounds.play('tap');

            if (window.TapticEngine) {
                window.TapticEngine.impact({ style: "light" });
            }

            if (window.AdMob) {
                // Prepare
                window.AdMob.prepareRewardVideoAd({ adId: this.state.rewardedId });
            }
            this.startHintPulseTimer();
        }
    }



    /*
    * Render
    */

    render() {

        let currentWord = (this.state.pulsingLetters.length === 0 ? this.getSelectedWord() : this.getHintWord());

        return (
            <div
                className="BoardOuter"
                style={{
                    //paddingTop: '20px',
                    backgroundColor: (this.state.transitioning ? this.state.colors.primary : Constants.outerColor),
                }}
            >
                <div
                    ref={this.boardWrapper}
                    className="BoardWrapper"
                    style={{
                        backgroundColor: (this.state.transitioning ? this.state.colors.primary : Constants.outerColor),
                    }}
                    // Mouse events
                    onMouseDown={(e) => {
                        this.setState({
                            mouseDown: true
                        });
                        const coord = this.getRelativeCoordinates(
                            this.boardContainer.current,
                            e.clientX,
                            e.clientY
                        );
                        this.handleStart(coord.x, coord.y);
                    }}
                    onMouseMove={(e) => {
                        if (this.state.mouseDown) {
                            const coord = this.getRelativeCoordinates(
                                this.boardContainer.current,
                                e.clientX,
                                e.clientY
                            );
                            this.handleMove(coord.x, coord.y);
                        }
                    }}
                    onMouseUp={(e) => {
                        this.setState({
                            mouseDown: false
                        });
                        this.handleEnd();
                    }}
                    // Touch events
                    onTouchStart={(e) => {
                        e.preventDefault();
                        // Touch is valid
                        if (e.targetTouches.length === 1) {
                            this.setState({
                                touchValid: true
                            });
                            const coord = this.getRelativeCoordinates(
                                this.boardContainer.current,
                                e.targetTouches[0].pageX,
                                e.targetTouches[0].pageY
                            );
                            this.handleStart(coord.x, coord.y);
                        }
                        // Touch is invalid
                        else {
                            this.setState({
                                touchValid: false
                            });
                            this.handleEnd();
                        }
                    }}
                    onTouchMove={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (this.state.touchValid) {
                            const coord = this.getRelativeCoordinates(
                                this.boardContainer.current,
                                e.targetTouches[0].pageX,
                                e.targetTouches[0].pageY
                            );
                            this.handleMove(coord.x, coord.y);
                        }
                    }}
                    onTouchEnd={(e) => {
                        e.preventDefault();
                        // If there are no remaining touches
                        if (e.touches.length === 0) {
                            // If this was a valid touch, fire handleEnd normal
                            if (this.state.touchValid) {
                                this.handleEnd();
                            }
                        }
                    }}
                >
                    <div className="BoardContainer">
                        {/* Transform board */}
                        <div
                            ref={this.boardTransform}
                            style={{
                                width: '100%',
                                height: '100%',
                                position: 'absolute',
                                transform: 'translateY(0) rotate(0deg)',
                                willChange: 'transform',
                                transition: 'transform 1000ms',
                            }}
                        >
                            {/* Board container */}
                            <div
                                ref={this.boardContainer}
                                style={{
                                    ...Styles.BoardContainer,
                                    width: (this.state.width * Constants.tileSize) + 'px',
                                    height: (this.state.height * Constants.tileSize) + 'px',
                                    transform: 'translate(-50%, -50%)',
                                    backgroundColor: (this.state.transitioning ? this.state.colors.primary : Constants.outerColor),
                                    top: '50%',
                                    left: '50%',
                                }}
                            >
                                {/* Selected word */}
                                <div
                                    style={{
                                        ...Styles.SelectedWordWrapper,
                                        transform: 'translate(-50%, -30px) scale(' + (currentWord ? '1' : '0') + ')',
                                        opacity: (this.state.pulsingLetters.length === 0 ? '1' : '0.2'),
                                    }}
                                >
                                    <div
                                        style={{
                                            ...Styles.SelectedWordFade,
                                            left: '0',
                                            backgroundImage: 'linear-gradient(to right, ' + (this.state.transitioning ? this.state.colors.secondary : Constants.outerColor) + ', rgba(255,255,255,0))',
                                        }}
                                    />
                                    <div
                                        style={{
                                            ...Styles.SelectedWordFade,
                                            right: '0',
                                            backgroundImage: 'linear-gradient(to left, ' + (this.state.transitioning ? this.state.colors.secondary : Constants.outerColor) + ', rgba(255,255,255,0))',
                                        }}
                                    />
                                    {currentWord}
                                </div>
                                {this.getTiles()}
                                {this.getLetters()}
                            </div>
                        </div>

                        {/* Transform level number */}
                        <div
                            ref={this.levelNumberTransform}
                            style={{
                                width: '100%',
                                height: '100%',
                                position: 'absolute',
                                transform: 'translateY(100%) rotate(-30deg)',
                                willChange: 'transform',
                                transition: 'transform 1000ms',
                            }}
                        >
                            {/* Level number */}
                            <div
                                ref={this.levelNumber}
                                style={{
                                    position: 'absolute',
                                    transform: 'translate(-50%, -50%)',
                                    top: '50%',
                                    left: '50%',
                                    color: '#fff',
                                }}
                            >
                                <div style={{
                                    fontSize: Constants.levelNumberSize + 'px',
                                    fontFamily: '"Roboto Condensed", sans-serif',
                                    fontWeight: '300',
                                }}>
                                    {this.state.level + 1}
                                </div>
                                <div style={{
                                    fontSize: '25px',
                                    textAlign: 'center',
                                    position: 'absolute',
                                    left: '50%',
                                    top: '100%',
                                    transform: 'translateX(-50%)',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {this.state.theme}
                                </div>
                            </div>
                        </div>

                        <BoardButtons
                            // Pulsing
                            pulsingHint={this.state.pulsingHintButton}
                            pulsingRestart={!this.state.solvable}
                            // Showing
                            showingMenu={this.state.showingMenuButton}
                            showingHint={(this.state.showingHintButton && this.state.solvable && this.state.pulsingLetters.length === 0 && window.AdMob)}
                            showingRestart={this.state.showingRestartButton}
                            // Restart button rotations
                            restartRotations={this.state.restartRotations}
                            // Click callbacks
                            onShowMenu={() => {
                                this.handleMenuOpen();
                            }}
                            onUseHint={() => {
                                this.handleHint();
                            }}
                            onRestart={() => {
                                this.handleRestart();
                            }}
                        />

                        <ThemeHeader
                            level={this.state.level}
                            theme={this.state.theme}
                            colors={this.state.colors}
                            showing={(!this.state.transitioning && (!this.state.tutorialMode || (this.state.tutorialMode && this.state.tutorialStage >= 3)))}
                        />

                    </div>
                </div>
                <Tutorial
                    ref={this.tutorial}
                    showing={this.state.tutorialMode}
                    currentStage={0}
                    transitioning={this.state.transitioning}
                    onStageChange={(stage) => {
                        this.setState({tutorialStage: stage})

                        if (stage === 2) {
                            this.fadeInLetters();
                        }
                        else if (stage === 4) {
                            this.setState({
                                pulsingLetters: [0, 2, 3, 7]
                            }, () => {
                                this.restartIntervals();
                            });
                        }
                        else if (stage === 5) {
                            this.setState({
                                pulsingLetters: [9, 14, 10, 11, 12, 15]
                            }, () => {
                                this.restartIntervals();
                            });
                        }
                        else if (stage === 7) {
                            this.setState({
                                pulsingLetters: [1, 6, 5, 4, 8, 13]
                            }, () => {
                                this.restartIntervals();
                            });
                        }
                        else if (stage === 9) {
                            setTimeout(() => {
                                this.tutorial.current.advanceStage();
                                this.setState({tutorialMode: false})
                            }, 2500);
                        }

                    }}
                />
            </div>
        );
    }
}

export default Board;
